import { type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";

import { FormControlWrapper } from "components/Shared";
import { TextBox, MultiSelect } from "components/Shared/Inputs";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";
import { RISK_DESCRIPTION_MAX_LENGTH } from "../../RiskAssessments.constants";
import { useRiskAssessmentFieldActionsContext } from "../../hooks/RiskAssessmentFieldActionsContext";

export const RiskFactorsSection: FunctionComponent = () => {
  const { t } = useTranslation("criticalIncident");
  const {
    basePath,
    record,
    recordTouched,
    recordErrors,
    isReadonly,
    riskFactorLevelOptions,
    riskFactorOptions,
    handleParticipantBooleanChange,
    handleParticipantRiskFactorLevelChange,
    makeParticipantChangeHandler,
    handleRiskFactorsChange,
  } = useRiskAssessmentFieldActionsContext();
  const hasAnyRiskFactor = Boolean(record.riskOfSuicide) || Boolean(record.riskOfSelfHarm) || Boolean(record.riskOfHarmToFromOthers);

  return (
    <>
      <Grid item sm={12}>
        <FormControlWrapper
          isReadonly={isReadonly}
          type="radio.yesno"
          label={t("risk_of_suicide")}
          name={`${basePath}.riskOfSuicide`}
        >
          <ChooseYesNo
            label={t("risk_of_suicide")}
            name={`${basePath}.riskOfSuicide`}
            value={record.riskOfSuicide}
            onChange={handleParticipantBooleanChange("riskOfSuicide")}
          />
        </FormControlWrapper>
      </Grid>
      {record.riskOfSuicide && (
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="multiselect"
            label={t("suicide_risk_factor")}
            name={`${basePath}.suicideRiskFactors`}
            options={riskFactorLevelOptions}
            fullWidth
          >
            <MultiSelect
              label={t("suicide_risk_factor")}
              name={`${basePath}.suicideRiskFactors`}
              options={riskFactorLevelOptions}
              value={record.suicideRiskFactors}
              onChange={handleParticipantRiskFactorLevelChange("suicideRiskFactors")}
            />
          </FormControlWrapper>
        </Grid>
      )}

      <Grid item sm={12}>
        <FormControlWrapper
          isReadonly={isReadonly}
          type="radio.yesno"
          label={t("risk_of_self_harm_injury")}
          name={`${basePath}.riskOfSelfHarm`}
        >
          <ChooseYesNo
            label={t("risk_of_self_harm_injury")}
            name={`${basePath}.riskOfSelfHarm`}
            value={record.riskOfSelfHarm}
            onChange={handleParticipantBooleanChange("riskOfSelfHarm")}
          />
        </FormControlWrapper>
      </Grid>
      {record.riskOfSelfHarm && (
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="multiselect"
            label={t("self_harm_risk_factor")}
            name={`${basePath}.selfHarmRiskFactors`}
            options={riskFactorLevelOptions}
            fullWidth
          >
            <MultiSelect
              label={t("self_harm_risk_factor")}
              name={`${basePath}.selfHarmRiskFactors`}
              options={riskFactorLevelOptions}
              value={record.selfHarmRiskFactors}
              onChange={handleParticipantRiskFactorLevelChange("selfHarmRiskFactors")}
            />
          </FormControlWrapper>
        </Grid>
      )}

      <Grid item sm={12}>
        <FormControlWrapper
          isReadonly={isReadonly}
          type="radio.yesno"
          label={t("risk_of_harm_to_from_others")}
          name={`${basePath}.riskOfHarmToFromOthers`}
        >
          <ChooseYesNo
            label={t("risk_of_harm_to_from_others")}
            name={`${basePath}.riskOfHarmToFromOthers`}
            value={record.riskOfHarmToFromOthers}
            onChange={handleParticipantBooleanChange("riskOfHarmToFromOthers")}
          />
        </FormControlWrapper>
      </Grid>
      {record.riskOfHarmToFromOthers && (
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="multiselect"
            label={t("harm_to_from_others_risk_factor")}
            name={`${basePath}.harmToFromOthersRiskFactors`}
            options={riskFactorLevelOptions}
            fullWidth
          >
            <MultiSelect
              label={t("harm_to_from_others_risk_factor")}
              name={`${basePath}.harmToFromOthersRiskFactors`}
              options={riskFactorLevelOptions}
              value={record.harmToFromOthersRiskFactors}
              onChange={handleParticipantRiskFactorLevelChange("harmToFromOthersRiskFactors")}
            />
          </FormControlWrapper>
        </Grid>
      )}

      {hasAnyRiskFactor && (
        <>
          <Grid item sm={12}>
            <FormControlWrapper
              isReadonly={isReadonly}
              label={t("describe_risk")}
              name={`${basePath}.riskDescription`}
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={t("describe_risk")}
                name={`${basePath}.riskDescription`}
                value={record.riskDescription ?? ""}
                onChange={makeParticipantChangeHandler("riskDescription")}
                slotProps={{ input: { maxLength: RISK_DESCRIPTION_MAX_LENGTH } }}
                error={Boolean(recordTouched?.riskDescription) && Boolean(recordErrors?.riskDescription)}
                helperText={recordTouched?.riskDescription ? t(recordErrors?.riskDescription || " ") : ""}
              />
            </FormControlWrapper>
          </Grid>

          <Grid item sm={12}>
            <FormControlWrapper
              isReadonly={isReadonly}
              type="multiselect"
              label={t("indicate_risk_factors")}
              name={`${basePath}.riskFactors`}
              options={riskFactorOptions}
              fullWidth
            >
              <MultiSelect
                label={t("indicate_risk_factors")}
                name={`${basePath}.riskFactors`}
                options={riskFactorOptions}
                value={record.riskFactors}
                onChange={handleRiskFactorsChange()}
              />
            </FormControlWrapper>
          </Grid>
        </>
      )}
    </>
  );
};
